"use client"

import {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  type ReactNode,
} from "react"
import Link from "next/link"
import Image from "next/image"
import { m, AnimatePresence } from "framer-motion"
import { ClientOnly } from "@/lib/animation/client-only"
import { easeOutQuart, expoOut } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { EllaTestSection } from "@/components/chat/ella-test-section"
import {
  type TestData,
  TEST_KEY_PREFIX,
  diseasesSubheadingKeyFor,
  explorerDefaultPackageFor,
  getTestBySlug,
  IMAGING_ADDONS,
  BLOOD_ADDONS,
} from "@/data/tests"
import { isAvailable } from "@/lib/availability"
import { visibleCrossSellSlugs } from "./cross-sell-visibility"
import { track } from "@/lib/analytics"
import { PRODUCT_IDS } from "@/components/ui/booking-cta"
import { cmsBookingProductId } from "@/lib/services-overlay"
import { ServiceBookingCta } from "@/components/ui/service-booking-cta"
import {
  resolveServiceAvailability,
  isWaitlistState,
  type ServiceAvailability,
} from "@/lib/service-availability"
import type { CmsServiceConfig } from "@/lib/cms"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { type ProductCategory } from "@/i18n/config"
import { HowItWorksScroll } from "@/components/ui/how-it-works-scroll"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import { QuizCtaBanner } from "@/components/marketing/quiz-cta-banner"
import { useCountry } from "@/components/layout/country-provider"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isAllowedInCountry } from "@/lib/country-gates"
import { TestHeroPrice } from "@/components/ui/test-hero-price"

/** Slugs where the hero "see what your insurance covers" CTA appears
 *  above the title: the two main products plus every blood-test add-on. */
const INSURANCE_CTA_SLUGS = new Set<string>([
  "full-body-mri",
  "advanced-blood-test",
  ...BLOOD_ADDONS,
])

import {
  StarIcon,
  ImpactAreaIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SmartphoneIcon,
} from "@/components/ui/icons"
import { QuickFactsSection } from "@/components/ui/quick-facts-section"
import { TestimonialCardsRow } from "@/components/ui/testimonial-cards-row"
import { TestsStepsTimeline } from "@/components/ui/tests-steps-timeline"
import { ReviewedByPhysiciansIcon } from "@/components/ui/reviewed-by-physicians-icon"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { FaqSection } from "@/components/ui/faq-section"
import { ServicesCarousel } from "@/components/ui/services-carousel"
import { useTranslatedServices } from "@/lib/use-translated-services"
import { QuestionsHelpSection } from "@/components/ui/questions-help-section"
import { canLinkArticle } from "./article-link-visibility"
import { TestimonialsSection } from "@/app/(frontend)/[country]/[locale]/_components/testimonials-section"
import { Button, ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { entryMriTierKey } from "@/data/mri-packages"
import { BloodTestCard } from "@/components/ui/blood-test-card"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { BloodAddonsSection } from "@/components/ui/blood-addons-section"
import { ImagingAddonsSection } from "@/components/ui/imaging-addons-section"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { FindingsLegend } from "@/components/ui/findings-legend"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { cmsMediaUrl, type Testimonial } from "@/lib/cms"
import { useNewTestsPagesEnabled } from "@/lib/use-new-tests-pages"
import { blobMediaUrl } from "@/lib/blob-url"
import { AddonFindsCards } from "@/components/tests/addon-finds-cards"

/* ───────────────────── Google icon ───────────────────── */

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.07 24.07 0 0 0 0 21.56l7.98-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  )
}

/* ───────────────────── Shared data ───────────────────── */

/* ───────────────────── Main component ───────────────────── */

/** Map test slug → biomarker page filter param for "see all biomarkers" link.
 *  Imaging add-ons (mamma-mri, calcium-score, body-composition, brain-volumetry)
 *  are intentionally omitted — they don't have dedicated biomarker filters and
 *  linking to /biomarkers/core is misleading. The link is hidden when a test
 *  has no entry here. The full-body MRI links to the market's entry tier
 *  (`entryMriTierKey`), resolved in the component. */
const BIOMARKER_FILTER_BY_TEST: Record<string, string> = {
  "advanced-blood-test": "blood",
  "advanced-blood-panel": "blood",
  "omega-blood-test": "addon-omega",
  "essential-b-vitamins-test": "addon-bvitamins",
  "hormone-blood-test-women": "blood",
  "hormone-blood-test-men": "blood",
  "trace-elements-blood-test": "addon-trace-elements",
  "heavy-metals-blood-test": "addon-heavy-metals",
  "coeliac-blood-test": "addon-coeliac",
}

/** Slug → the imaging add-on card that IS this page, used to hide it from the
 *  sibling add-on grid. `""` means the test is an imaging add-on but has no
 *  card in that grid (brain volumetry is included in Pro, not sold on its
 *  own), so the grid renders in full. Slugs absent here get no grid. */
const IMAGING_ADDON_TITLE_KEY: Record<string, string> = {
  "body-composition": "addonBodyComposition",
  "calcium-score": "addonHeartCT",
  "mamma-mri": "addonMammaMRI",
  "dexa-scan": "addonDEXA",
  "gender-health": "addonGenderHealth",
  "brain-volumetry": "",
}

/** Same idea for the blood add-on grid. */
const BLOOD_ADDON_TITLE_KEY: Record<string, string> = {
  "essential-b-vitamins-test": "addonBVitamins",
  "omega-blood-test": "addonOmega",
  "hormone-blood-test-women": "addonHormoneWomen",
  "hormone-blood-test-men": "addonHormoneMen",
}

/** Testimonial bar — shows one relevant text-only review at a time;
 *  user steps through with arrow buttons. Service-scoped by the server
 *  testimonials fetch on the test page.
 *
 *  Currently unrendered: `TestimonialCardsRow` replaced it in Section 4. Kept
 *  (with its call site commented out there) so we can switch back cheaply —
 *  delete both once the card row is settled. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- see above
function CyclingTestimonialBar({
  customerCountLabel,
  cmsTestimonials,
}: {
  customerCountLabel: string
  cmsTestimonials?: Testimonial[]
}) {
  // CMS text reviews only, normalized into a single
  // `{ id, quote, author, avatarSrc, source }` shape for the render below.
  // Per the testimonials model (see TestimonialCard), the review text lives in
  // `headline` — the lead-in shown in quotes — with `quote` an optional longer
  // body that's empty when the whole text already sits in the headline. Join
  // both so the bar shows the full review instead of an empty `“”`.
  const reviews = useMemo(
    () =>
      (cmsTestimonials ?? [])
        .filter((c) => c.type === "text")
        .map((c) => ({
          id: c.id,
          quote: [c.headline, c.quote].filter(Boolean).join(" ").trim(),
          author: c.authorName,
          avatarSrc: c.avatar?.url ? cmsMediaUrl(c.avatar.url) : undefined,
          source: c.source,
        }))
        .filter((r) => r.quote),
    [cmsTestimonials]
  )
  const [index, setIndex] = useState(0)
  // Measured height of the currently-visible testimonial — drives a smooth
  // height transition on the wrapper so longer quotes don't cause a jump.
  // Using ref-as-state so the measurement effect re-runs whenever
  // AnimatePresence swaps in a new child element.
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | "auto">("auto")
  // The keyed-swap AnimatePresence below is mount-gated via <ClientOnly>
  // (lib/animation/client-only). At SSR the review renders inside a
  // plain <div> so the static shell contains the quote without any
  // framer-motion wrapper (PresenceChild's useId / Math.random would
  // otherwise either bail the whole page to CSR or inject a per-request
  // <!--$?--> marker into <main>). After hydration the gate flips on
  // and subsequent review swaps animate normally. The inline pattern
  // (rather than wrapping with <AnimatedSwap>) is because we also
  // need a ref on the inner node for the ResizeObserver-driven height
  // transition, which AnimatedSwap doesn't expose.

  const prev = () => setIndex((i) => (i - 1 + reviews.length) % reviews.length)
  const next = () => setIndex((i) => (i + 1) % reviews.length)

  // Track the tallest testimonial measured so far — the wrapper grows to
  // accommodate longer quotes but never shrinks, so content below doesn't
  // jump up and down as reviews cycle. ResizeObserver covers text reflow
  // from viewport resizes and font loads. jsdom returns 0 for offsetHeight,
  // so only commit positive measurements.
  useLayoutEffect(() => {
    if (!contentNode) return
    const measure = () => {
      const next = contentNode.offsetHeight
      if (next > 0) {
        setHeight((prev) =>
          typeof prev === "number" && prev >= next ? prev : next
        )
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(contentNode)
    return () => ro.disconnect()
  }, [contentNode])

  const review = reviews[index]
  if (!review) return null

  return (
    <section className="py-6">
      <div>
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="bg-aubergine-950 flex flex-col items-start gap-6 rounded-[12px] p-4 md:flex-row md:gap-9 md:p-10">
            <div className="flex-shrink-0">
              <a
                href="https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m18!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5"
              >
                <GoogleIcon />
                {Array.from({ length: 5 }, (_, i) => (
                  <StarIcon key={i} width={14} height={14} />
                ))}
              </a>
              <p className="type-body-lg mt-2 font-light text-white">
                {customerCountLabel}
              </p>
            </div>
            <div className="max-w-[640px] flex-1">
              {(() => {
                const reviewBody = (
                  <>
                    <p className="type-title-md font-normal text-white">
                      &ldquo;{review.quote}&rdquo;
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      {review.avatarSrc && (
                        <div className="relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-white/20">
                          <Image
                            src={review.avatarSrc}
                            alt=""
                            fill
                            className="object-cover"
                          />
                        </div>
                      )}
                      <p className="type-body-lg font-light text-white/60">
                        {review.author} ·{" "}
                        {review.source === "google" ? "Google" : "Trustpilot"}
                      </p>
                    </div>
                  </>
                )
                return (
                  <m.div
                    className="relative overflow-hidden"
                    animate={{ height }}
                    transition={{
                      duration: 0.5,
                      ease: easeOutQuart as unknown as number[],
                    }}
                  >
                    <ClientOnly
                      fallback={<div ref={setContentNode}>{reviewBody}</div>}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <m.div
                          key={review.id}
                          ref={setContentNode}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{
                            duration: 0.4,
                            ease: expoOut as unknown as number[],
                          }}
                        >
                          {reviewBody}
                        </m.div>
                      </AnimatePresence>
                    </ClientOnly>
                  </m.div>
                )
              })()}
              {reviews.length > 1 && (
                <div className="mt-6 flex gap-2">
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous"
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white/60 hover:text-white"
                  >
                    <ChevronLeftIcon width={16} height={16} />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next"
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white/60 hover:text-white"
                  >
                    <ChevronRightIcon width={16} height={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function TestDetailClientClassic({
  test,
  cmsBookingProductId: cmsBookingProductIdProp,
  servicesConfig = [],
  showNewTestsPages = false,
  heroLivePrice,
  packagesFromPrice,
  packagesFromPriceBlood,
  heroLiveMemberChf,
  initialExperts = [],
  initialFaqs = [],
  initialTestimonials = [],
  testimonialCardsRowSlot,
  testimonialsSlot,
  faqSlot,
  ellaEligible = false,
  ellaScope = "tests",
  ellaTestName = "",
  visibleArticleSlugs = [],
}: {
  test: TestData
  /** Booking product for this market, resolved page-side (CMS id, or the
   *  market's own product under the page's price key). */
  cmsBookingProductId?: string
  /** CMS products control plane — drives the per-market booking-CTA notice. */
  servicesConfig?: CmsServiceConfig[]
  /** test-detail-ella-v1 — mount gate for the under-fold Ella assistant section
   *  (computed page-side: contextual-chat-eligible + not DE). The variant read
   *  itself happens client-side inside the section. */
  ellaEligible?: boolean
  /** Chat routing scope for the Ella section ("fullbodymri" | "tests"). */
  ellaScope?: string
  /** Localized test title, used for the Ella heading + page context. */
  ellaTestName?: string
  showNewTestsPages?: boolean
  /** Live Stripe regular price (CHF) for the hero; falls back to `test.price`
   *  when absent (slug has no price key, or the feed was unavailable). */
  heroLivePrice?: number
  packagesFromPrice?: string
  packagesFromPriceBlood?: string
  /** Live Stripe member price (CHF) for the hero; falls back to
   *  `test.memberPrice` when null. */
  heroLiveMemberChf?: number | null
  initialExperts?: ReviewerExpert[]
  initialFaqs?: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
  initialTestimonials?: Testimonial[]
  /** Streamed CMS sections — async server components in `<Suspense>`,
   *  provided by the page so a slow CMS delays only the section. Each takes
   *  precedence over its `initial*` data prop when present. */
  testimonialCardsRowSlot?: ReactNode
  testimonialsSlot?: ReactNode
  faqSlot?: ReactNode
  /** Insights slugs visible in this country — gates the question cards'
   *  "Learn more" links (see `canLinkArticle`). */
  visibleArticleSlugs?: string[]
}) {
  const showNewTestsPagesEnabled = useNewTestsPagesEnabled(showNewTestsPages)
  const ut = useTranslations("ui")
  const tt = useTranslations("tests")
  // Only for the steps section's "How it works" eyebrow — the `tests`
  // namespace has no equivalent and this reuses the existing nav string.
  const nt = useTranslations("nav")
  const chatT = useTranslations("chat")
  const locale = useLocale()

  const pt = useTranslations("pricing")
  const rpt = useTranslations("report")
  // Blood add-on section heading + "for members" suffix (shared with
  // /go/bloodtest); only rendered on the advanced-blood-test page below.
  const gt = useTranslations("go")
  // [TODO: confirm DE pricing] — `test.price` is a CHF figure. On EUR markets
  // we don't yet have authoritative prices, so hide the price line entirely
  // rather than mislabel the CHF number as EUR.
  const { code: country, currency } = useCountry()
  const biomarkerFilter =
    test.slug === "full-body-mri"
      ? entryMriTierKey(country)
      : BIOMARKER_FILTER_BY_TEST[test.slug]
  const showPrice = currency === "CHF"
  // Membership markets (CH + AT): the hero price leads with member /
  // membership pricing. Country-derived, so the prerendered file already
  // matches — no flicker between the one-off and the member price.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  const allServices = useTranslatedServices({
    showNewTestsPages: showNewTestsPagesEnabled,
  })
  // Cross-sell slugs already filtered to what this market offers: the route
  // gate (CH-only pages soft-404 when linked from de/nl), the per-test
  // availability deny-map (advanced-blood-test is `{ "de-de": false }` — hidden
  // and 308-redirected in DE), and the imaging category gate (DE/NL sell blood
  // only, so the MRI line-up is coming-soon, not bookable). The classic layout
  // doesn't run the CMS overlay (`applyServicesConfig`) the redesigned one does,
  // so this is the single gate. See `./cross-sell-visibility`.
  const crossSellSlugs = visibleCrossSellSlugs(test.slug, country, locale)
  const translatedServices = allServices.filter((s) =>
    crossSellSlugs.some((slug) => s.path.endsWith(`/${slug}`))
  )
  // The "packages" section (Section 8) advertises a flagship product: the
  // full-body MRI (`MriPackageCards`, mri pricingType), the advanced blood test
  // with the membership comparison (`BloodTrackingComparison`, on its own page),
  // or — the else branch — the advanced-blood-test `BloodTestCard` for every
  // other blood page. On a page whose own product is neither flagship (e.g.
  // prevention-starter, the DE-only entry-level package) that else card
  // advertises the advanced blood test, which is `{ "de-de": false }` — hidden
  // and 308-redirected to prevention-starter in DE. Don't render a packages card
  // for a product this market can't book; gate on the advertised product's own
  // availability, not a country literal. MRI pages and the advanced-blood-test
  // page show their own product, so they're unaffected.
  const packagesShowsAdvancedBlood =
    test.pricingType !== "mri" && test.slug !== "advanced-blood-test"
  const showPackagesSection =
    !packagesShowsAdvancedBlood ||
    isAvailable(
      getTestBySlug("advanced-blood-test")?.availability,
      country,
      locale
    )
  const keyPrefix = TEST_KEY_PREFIX[test.slug] || ""
  const diseasesSubheadingKey = diseasesSubheadingKeyFor(test.slug)
  const lp = useLocalePath()
  // CMS first, static map second. `services.bookingProductId` is the control
  // plane: it lets a product become bookable by editing a CMS row, with no
  // deploy — which is the whole point of the `products` collection. The
  // hardcoded mapping stays as the fallback for everything the CMS has no row
  // for. Without this, a product the CMS calls bookable still renders the
  // coming-soon/waitlist CTA below, because `!bookingProductId` downgrades it
  // (Vorsorge-Starter shipped that way: `lifecycle: live` + a
  // `bookingProductId` in the CMS, "Bald verfügbar" on the page).
  const bookingProductId =
    cmsBookingProductIdProp ??
    cmsBookingProductId(servicesConfig, `/tests/${test.slug}`) ??
    (test.bookingProductId ? PRODUCT_IDS[test.bookingProductId] : undefined)
  // Full-body MRI + its imaging add-ons (DEXA, mamma-MRI, calcium score, …)
  // are expansion products: in a `checkup` market they show the waitlist
  // modal until the market reaches `launched`. Blood tests book immediately.
  const ctaCategory: ProductCategory =
    test.slug === "full-body-mri" || IMAGING_ADDONS.includes(test.slug)
      ? "mri"
      : "blood"
  // Single availability verdict for this test in this market (resolved once so
  // the path/slug join runs a single time per page). Composes the per-service
  // CMS status with the country/category lifecycle in one fixed precedence —
  // so the pill and the CTA can't disagree (no more independent coming-soon
  // pill + not-available CTA on the same card).
  const resolvedAvailability = resolveServiceAvailability({
    country,
    category: ctaCategory,
    pathOrSlug: test.slug,
    servicesConfig,
  })
  // A test with no booking product wired (no static `PRODUCT_IDS` mapping and
  // no CMS override — e.g. prevention-starter, Task 7) has nothing for the CTA
  // to link to. Without a CMS row, `resolveServiceAvailability` defaults to
  // "bookable", which would otherwise send the CTA to the generic booking app
  // with no product pre-selected. Downgrade to the existing coming-soon/
  // waitlist affordance instead of ever linking to a blank product.
  const availability: ServiceAvailability =
    resolvedAvailability === "bookable" && !bookingProductId
      ? "coming_soon"
      : resolvedAvailability
  // The hero pill shows for coming-soon / waitlist states only — `not_available`
  // resolves to its own state, so it never also shows a coming-soon pill.
  const heroComingSoon = isWaitlistState(availability)
  // Coming-soon CTAs open the waitlist modal, so both the visible label AND
  // the analytics `trackingLabel` read "join waitlist" rather than "book
  // check-up" — the recorded click matches what the user saw and did (the
  // `cta_click` href is already "waitlist" in this branch).
  const ctaLabel = heroComingSoon ? ut("joinWaitlist") : ut("bookCheckup")
  const trackingLocation = `test-detail-${test.slug}`
  // Which add-on card (if any) represents THIS page, so the sibling add-on
  // grids below can hide it. `undefined` = not an imaging add-on at all (no
  // grid); `""` = an imaging add-on with no card of its own (brain volumetry
  // isn't sold separately), so nothing to exclude.
  const ownImagingAddonTitleKey = IMAGING_ADDON_TITLE_KEY[test.slug]
  const ownBloodAddonTitleKey = BLOOD_ADDON_TITLE_KEY[test.slug]
  const experts = initialExperts
  // Blood-test pages sell no scan: the radiologist experts block ("reviewed by
  // our renowned radiologists") and the MRI sample summary in the report
  // mockup don't apply there (AHD-13181).
  const isBloodTestPage = ctaCategory === "blood"
  // Experts module (Section 14): hidden on DE for now (AHD-6680), where the
  // Swiss-physician credibility module isn't shown on the test pages, and on
  // AT, where it is off on every page (DoctorsSection does the same).
  // country-gate-ignore: DE checkup-phase / AT content hide, not a page gate
  const hideExperts = country === "de" || country === "at"
  // Cross-sell carousel ("Perfekt kombinierbar", Section 12): off on AT
  // until more Austrian test pages are back; today it would offer only the
  // analyses bundled into Advanced and Pro.
  // country-gate-ignore: AT content hide, not a page gate
  const hideCrossSell = country === "at"
  const hasLocalDiseases =
    test.diseaseCategories.length > 0 &&
    test.diseaseCategories.some((c) => c.diseases.length > 0)
  const hasBiomarkerConfig = (test.biomarkerPackages?.length ?? 0) > 0

  const testTitle = tt(`${keyPrefix}Title` as never)
  const testBadge = tt(`${keyPrefix}Badge` as never)
  const testDescription = tt(`${keyPrefix}Desc` as never)
  /** Resolve a step translation key, applying the per-test prefix when set */
  const sk = (key: string) => {
    if (!test.stepsKeyPrefix) return key
    return `${test.stepsKeyPrefix}${key.charAt(0).toUpperCase()}${key.slice(1)}`
  }
  const stepsImages = test.stepsImages ?? [
    blobMediaUrl("/images/steps/before.jpg"),
    blobMediaUrl("/images/steps/during.jpg"),
    blobMediaUrl("/images/steps/after.jpg"),
  ]

  useEffect(() => {
    track.viewItem(testTitle, testBadge)
  }, [test.slug, testTitle, testBadge])

  const questions = test.questions.map((q, i) => {
    const readingKey = (part: "Value" | "Caption" | "Range" | "Status") =>
      `${keyPrefix}Q${i + 1}${part}` as never
    // Brain volumetry and omega catalogs carry a report-style reading for the
    // hero result cards (AHD-13079): the value with its unit and the
    // reference range, with the market's decimal separator, plus an optional
    // caption for what the unit is a share of and an optional status label
    // (the omega card's "Optimal", matching the homepage card). Tests without
    // these keys keep the plain data value and show no range: their data
    // ranges were never displayed and are not vetted.
    const hasReading = tt.has(readingKey("Value"))
    return {
      ...q,
      quote: tt(`${keyPrefix}Q${i + 1}` as never),
      explanation: tt(`${keyPrefix}A${i + 1}` as never),
      biomarkerName: tt(`${keyPrefix}Q${i + 1}Bio` as never),
      biomarkerValue: hasReading
        ? tt(readingKey("Value"))
        : q.biomarkerValueKey
          ? ut(q.biomarkerValueKey as never)
          : q.biomarkerValue,
      reading: hasReading
        ? {
            caption: tt.has(readingKey("Caption"))
              ? tt(readingKey("Caption"))
              : undefined,
            range: tt(readingKey("Range")),
          }
        : undefined,
      // One mapping for both hero cards: the first card used to know only
      // "negative", so omega's "observation" EPA card read "Normal range".
      statusLabel: tt.has(readingKey("Status"))
        ? tt(readingKey("Status"))
        : q.biomarkerStatus === "outOfRange"
          ? ut("outOfRange")
          : q.biomarkerStatus === "observation"
            ? ut("observation")
            : q.biomarkerStatus === "negative"
              ? ut("negative")
              : ut("normalRange"),
      articleHref: canLinkArticle(q.articleSlug, country, visibleArticleSlugs)
        ? lp(`/insights/${q.articleSlug}`)
        : undefined,
    }
  })

  return (
    <>
      {/* ═══════ Section 1: Hero ═══════ */}
      {/* Make area behind navbar white — hero bg extends under sticky nav */}
      {/* ═══════ Mobile hero: full-bleed background image ═══════ */}
      <section className="relative -mb-px h-lvh min-h-[600px] overflow-hidden md:hidden">
        <Image
          src={test.heroImage}
          alt={testTitle}
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />
        {/* Progressive blur on bottom third */}
        <div
          className="absolute inset-0"
          style={{
            maskImage: "linear-gradient(to bottom, transparent 55%, black 85%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 55%, black 85%)",
          }}
        >
          <Image
            src={test.heroImage}
            alt=""
            fill
            sizes="100vw"
            className="object-cover blur-[4px]"
          />
        </div>
        {/* Overlay — matches homepage mobile (solid 50% + bottom gradient for text) */}
        <div className="absolute inset-0 z-[1] bg-neutral-950/50" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-black/30" />

        <div className="absolute inset-0 z-[2] flex flex-col justify-end px-6 pt-[120px] pb-12">
          {INSURANCE_CTA_SLUGS.has(test.slug) && (
            // -ml-4 pulls the pill so its icon aligns with the title's
            // first letter rather than being inset by the pill's px-4.
            <div className="mb-4 -ml-4">
              <InsuranceCoverageCta size="md" icon="heart-check-pulse" dark />
            </div>
          )}
          <h1 className="type-display-lg break-words text-white">
            {testTitle}
          </h1>

          <p className="type-body-xl mt-4 text-white/85">{testDescription}</p>

          {heroComingSoon && (
            <ComingSoonPill
              label={ut("comingSoon")}
              variant="dark"
              className="mt-6"
            />
          )}
          <div className="mt-8">
            <div className="flex flex-col items-center gap-4">
              <ServiceBookingCta
                availability={availability}
                tone="dark"
                category={ctaCategory}
                productId={bookingProductId}
                trackingLabel={ctaLabel}
                trackingLocation={`${trackingLocation}-hero-mobile`}
                className="type-body-lg text-aubergine-900 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3.5 font-medium transition-opacity hover:opacity-90 md:px-8"
              >
                {ctaLabel}
              </ServiceBookingCta>
              <TestHeroPrice
                test={test}
                membershipFirst={isMembershipFirst}
                showPrice={showPrice}
                theme="dark"
                bookingProductId={bookingProductId}
                trackingLocation={trackingLocation}
                livePrice={heroLivePrice}
                liveMemberChf={heroLiveMemberChf}
              />
              {IMAGING_ADDONS.includes(test.slug) && (
                <p className="type-body-sm text-center text-white/60">
                  {pt.rich("imagingAddonsCombinationNote", {
                    mri: (chunks) => (
                      <a
                        href={lp("/tests/full-body-mri")}
                        className="underline underline-offset-2 hover:text-white"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
              {BLOOD_ADDONS.includes(test.slug) && (
                <p className="type-body-sm text-center text-white/60">
                  {pt.rich("bloodAddonsCombinationNote", {
                    blood: (chunks) => (
                      <a
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        className="underline underline-offset-2 hover:text-white"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
            </div>

            <m.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: { delayChildren: 0.4, staggerChildren: 0.08 },
                },
              }}
              className="mt-8 grid grid-cols-3 items-start gap-2"
            >
              {[
                {
                  Icon: ReviewedByPhysiciansIcon,
                  label: pt("trustReviewedByPhysicians"),
                },
                { Icon: ImpactAreaIcon, label: pt("trustPersonalActionPlan") },
                { Icon: SmartphoneIcon, label: pt("trustInteractiveReport") },
              ].map(({ Icon, label }) => (
                <m.div
                  key={label}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: expoOut as unknown as number[],
                      },
                    },
                  }}
                  className="flex min-w-0 flex-col items-center gap-1"
                >
                  <Icon size={24} className="text-white/70" />
                  <p className="text-center text-xs font-medium text-white/70">
                    {label}
                  </p>
                </m.div>
              ))}
            </m.div>
          </div>
        </div>
      </section>

      {/* ═══════ Desktop hero: two-column grid ═══════ */}
      <div className="-mt-[120px] hidden bg-white pt-[120px] md:block">
        <section className="pt-16 pb-16">
          <div className="mx-auto grid max-w-[1440px] grid-cols-2 items-center gap-8 px-4 md:px-10">
            {/* Left column — `min-w-0` so a long unbreakable word
                (e.g. German "Körperzusammensetzung") can wrap inside its
                grid column instead of overflowing into the image. */}
            <div className="min-w-0">
              {INSURANCE_CTA_SLUGS.has(test.slug) && (
                // -ml-4 pulls the pill so its icon aligns with the title's
                // first letter rather than being inset by the pill's px-4.
                <div className="mb-4 -ml-4">
                  <InsuranceCoverageCta size="md" icon="heart-check-pulse" />
                </div>
              )}
              {/* Desktop hero heading — ARIA role instead of native <h1> so
                  Lighthouse's one-h1-per-page audit stays green. The native
                  <h1> lives in the mobile hero above; that hero is
                  display:none at md+ so its <h1> is out of the accessibility
                  tree here, and screen readers at this breakpoint navigate
                  to this element as the page's heading level 1. */}
              <p
                className="type-display-lg break-words text-neutral-900"
                role="heading"
                aria-level={1}
              >
                {testTitle}
              </p>

              <p className="type-body-xl mt-4 max-w-lg text-neutral-900">
                {testDescription}
              </p>

              {heroComingSoon && (
                <ComingSoonPill label={ut("comingSoon")} className="mt-5" />
              )}
              <div className="mt-6 flex items-center gap-4">
                <ServiceBookingCta
                  availability={availability}
                  category={ctaCategory}
                  productId={bookingProductId}
                  trackingLabel={ctaLabel}
                  trackingLocation={`${trackingLocation}-hero-desktop`}
                >
                  <Button
                    variant="primary"
                    className="group"
                    iconRight={
                      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                    }
                  >
                    {ctaLabel}
                  </Button>
                </ServiceBookingCta>
                <TestHeroPrice
                  test={test}
                  membershipFirst={isMembershipFirst}
                  showPrice={showPrice}
                  theme="light"
                  bookingProductId={bookingProductId}
                  trackingLocation={trackingLocation}
                  livePrice={heroLivePrice}
                  liveMemberChf={heroLiveMemberChf}
                />
              </div>
              {IMAGING_ADDONS.includes(test.slug) && (
                <p className="type-body-sm mt-4 text-neutral-500">
                  {pt.rich("imagingAddonsCombinationNote", {
                    mri: (chunks) => (
                      <a
                        href={lp("/tests/full-body-mri")}
                        className="underline underline-offset-2 hover:text-neutral-700"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
              {BLOOD_ADDONS.includes(test.slug) && (
                <p className="type-body-sm mt-4 text-neutral-500">
                  {pt.rich("bloodAddonsCombinationNote", {
                    blood: (chunks) => (
                      <a
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        className="underline underline-offset-2 hover:text-neutral-700"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}

              <m.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: { delayChildren: 0.4, staggerChildren: 0.08 },
                  },
                }}
                className="mt-8 flex items-start gap-4"
              >
                {[
                  {
                    Icon: ReviewedByPhysiciansIcon,
                    size: 28,
                    label: pt("trustReviewedByPhysicians"),
                  },
                  {
                    Icon: ImpactAreaIcon,
                    size: 28,
                    label: pt("trustPersonalActionPlan"),
                  },
                  {
                    Icon: SmartphoneIcon,
                    size: 24,
                    label: pt("trustInteractiveReport"),
                  },
                ].map(({ Icon, size, label }) => (
                  <m.div
                    key={label}
                    variants={{
                      hidden: { opacity: 0, y: 8 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: expoOut as unknown as number[],
                        },
                      },
                    }}
                    className="flex w-[130px] flex-col items-center gap-1"
                  >
                    <div className="flex size-10 items-center justify-center">
                      <Icon size={size} className="text-neutral-500" />
                    </div>
                    <p className="type-title-sm text-center text-xs leading-4 font-medium text-neutral-900">
                      {label}
                    </p>
                  </m.div>
                ))}
              </m.div>
            </div>

            {/* Right column: hero image with floating cards inside */}
            <div className="relative">
              <div className="relative aspect-[1/1] w-full overflow-hidden rounded-[12px]">
                <Image
                  src={test.heroImage}
                  alt={testTitle}
                  fill
                  priority
                  sizes="500px"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />

                {questions.length > 0 && (
                  <m.div
                    initial={{ opacity: 0, y: 12, x: -8 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: expoOut as unknown as number[],
                      delay: 0.5,
                    }}
                    className={cn(
                      "absolute bottom-4 left-4 z-10 hidden max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block",
                      // Room for the range and status chips on one line.
                      questions[0].reading && "max-w-[260px]"
                    )}
                  >
                    <p className="type-body-lg font-medium text-black">
                      {questions[0].biomarkerName}
                    </p>
                    <span className="type-title-lg mt-0.5 block font-medium text-black">
                      {questions[0].biomarkerValue}
                    </span>
                    {questions[0].reading?.caption && (
                      <span className="type-body-sm block text-neutral-700">
                        {questions[0].reading.caption}
                      </span>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {questions[0].reading && (
                        <span className="type-label-md rounded-[37px] border border-neutral-900/15 px-[9px] py-[5px] font-medium whitespace-nowrap text-neutral-900">
                          {questions[0].reading.range}
                        </span>
                      )}
                      <span className="type-label-md inline-block rounded-[37px] bg-[rgba(184,199,101,0.52)] px-[9px] py-[5px] font-medium text-neutral-900">
                        {" "}
                        {/* ds-color-ignore: translucent overlay (alpha) */}
                        {questions[0].statusLabel}
                      </span>
                    </div>
                  </m.div>
                )}

                {questions.length > 1 && (
                  <m.div
                    initial={{ opacity: 0, y: -12, x: 8 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: expoOut as unknown as number[],
                      delay: 0.7,
                    }}
                    className={cn(
                      "absolute top-4 right-4 z-10 hidden max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block",
                      // Room for the range and status chips on one line.
                      questions[1].reading && "max-w-[260px]"
                    )}
                  >
                    <p className="type-body-lg font-medium text-black">
                      {questions[1].biomarkerName}
                    </p>
                    <span className="type-title-lg mt-0.5 block font-medium text-black">
                      {questions[1].biomarkerValue}
                    </span>
                    {questions[1].reading?.caption && (
                      <span className="type-body-sm block text-neutral-700">
                        {questions[1].reading.caption}
                      </span>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {questions[1].reading && (
                        <span className="type-label-md rounded-[37px] border border-neutral-900/15 px-[9px] py-[5px] font-medium whitespace-nowrap text-neutral-900">
                          {questions[1].reading.range}
                        </span>
                      )}
                      <span className="type-label-md inline-block rounded-[37px] bg-[rgba(184,199,101,0.52)] px-[9px] py-[5px] font-medium text-neutral-900">
                        {" "}
                        {/* ds-color-ignore: translucent overlay (alpha) */}
                        {questions[1].statusLabel}
                      </span>
                    </div>
                  </m.div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* test-detail-ella-v1 — assistant section, directly under the hero.
          Variant gate lives inside <EllaTestSection>. */}
      {ellaEligible && (
        <EllaTestSection
          scope={ellaScope}
          country={country}
          locale={locale}
          pageUrl={`/${country}/${locale}/tests/${test.slug}`}
          pageTitle={ellaTestName}
          testName={ellaTestName}
          bookingCta={
            <ServiceBookingCta
              availability={availability}
              category={ctaCategory}
              productId={bookingProductId}
              trackingLabel={chatT("section.ctaBook", {
                testName: ellaTestName,
              })}
              trackingLocation={`${trackingLocation}-ella`}
              className="type-title-sm bg-aubergine-800 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-medium text-white transition-[box-shadow] duration-200 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.1)]"
            >
              {chatT("section.ctaBook", { testName: ellaTestName })}
            </ServiceBookingCta>
          }
        />
      )}

      {/* "Why take this test" cards on the CH-only blood add-on pages,
          under the hero as on the membership-first site (AHD-13640). One
          render serves both sites: when merging the membership-first branch,
          keep this line and drop its `membershipFirstSite && <AddonFindsCards>`
          and the pricing-v5 `AddonFindsCards` import, or the cards show twice
          (flag on) or vanish from the current site (flag off). */}
      <AddonFindsCards slug={test.slug} />

      {/* ═══════ Section 2: Quick facts — V06 table layout ═══════ */}
      {/* Sits directly under the hero, matching the full-body-MRI page order:
          the practical "what is this / how long / who for" answers come before
          anything else. Shared with the redesigned detail page via
          <QuickFactsSection>: summary heading + CTAs on the left, one tinted
          table of labeled facts on the right. */}
      <QuickFactsSection
        facts={test.quickFacts}
        ctaButtons={
          <div className="mt-8 flex flex-col flex-wrap gap-3 sm:flex-row">
            <ServiceBookingCta
              availability={availability}
              category={ctaCategory}
              productId={bookingProductId}
              trackingLabel={ctaLabel}
              trackingLocation={`${trackingLocation}-quick-facts`}
              className={cn(
                buttonVariants({ variant: "primary" }),
                "w-full sm:w-auto"
              )}
            >
              {ctaLabel}
            </ServiceBookingCta>
            {biomarkerFilter && (
              <Link
                href={lp(`/biomarkers/${biomarkerFilter}`)}
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "w-full sm:w-auto"
                )}
              >
                {ut("viewBiomarkersList")}
              </Link>
            )}
          </div>
        }
      />

      {/* ═══════ Section 3: Questions we could help with ═══════ */}
      {/* Same `feature` treatment as the full-body-MRI page: heading + per-test
          sub-line on the left, 2x2 cards with photo top-right and a "Learn
          more" link on the cards that have a relevant insights article. */}
      {/* Guarded on `questions.length`: the section itself renders nothing
          without questions, but `description` would still eagerly resolve
          `<prefix>HelpSub` — and advanced-blood-panel (the one test with no
          questions) has no such key, so it threw MISSING_MESSAGE. */}
      {questions.length > 0 && (
        <QuestionsHelpSection
          heading={tt("questionsHeading")}
          description={tt(`${keyPrefix}HelpSub` as never)}
          questions={questions}
          learnMoreLabel={ut("learnMore")}
          variant="feature"
        />
      )}

      {/* ═══════ Section 4: Testimonials ═══════ */}
      {/* The dark single-quote bar it replaced is kept below, commented out,
          in case we want to switch back. `CyclingTestimonialBar` is still
          defined above this component for the same reason — delete both once
          the card row is settled.

      <CyclingTestimonialBar
        customerCountLabel={tt("customerCount")}
        cmsTestimonials={initialTestimonials}
      />

      */}
      {testimonialCardsRowSlot ?? (
        <TestimonialCardsRow
          customerCountLabel={tt("customerCount")}
          cmsTestimonials={initialTestimonials}
        />
      )}

      {/* ═══════ Section 5: Diseases & biomarkers ═══════ */}
      {(hasBiomarkerConfig || hasLocalDiseases) && (
        <BiomarkerDiseaseExplorer
          packages={test.biomarkerPackages ?? []}
          fallbackDiseases={test.diseaseCategories}
          isBloodTest={test.pricingType === "blood"}
          defaultTab={
            BLOOD_ADDONS.includes(test.slug) ? "biomarkers" : undefined
          }
          diseasesTab={
            diseasesSubheadingKey
              ? { subheading: tt(diseasesSubheadingKey as never) }
              : undefined
          }
          defaultPackageFilter={explorerDefaultPackageFor(test.slug, country)}
        />
      )}

      {/* ═══════ Section 6: The process ═══════ */}
      {test.processSteps?.length > 0 && (
        <HowItWorksScroll
          heading={tt("theProcess")}
          subtitle=""
          steps={test.processSteps?.map((step, i) => ({
            number: String(i + 1),
            color: step.color,
            title: tt(`${keyPrefix}Step${i + 1}Title` as never),
            description: tt(`${keyPrefix}Step${i + 1}Desc` as never),
            ctaLabel: step.ctaHref
              ? tt(`${keyPrefix}Step${i + 1}Cta` as never)
              : undefined,
            ctaHref: step.ctaHref,
          }))}
        />
      )}

      {/* ═══════ Section 7: Three simple steps ═══════ */}
      {/* Scroll-linked stepper, same treatment as /go/immunity, on a white
          card. Heading + step copy stay per-test (`sk(...)` keys); the CTA
          keeps this page's own "Book check-up" primary + "Talk to our team"
          secondary rather than the ad-LP's price-bearing button. */}
      <TestsStepsTimeline
        label={nt("howItWorks")}
        headingKey={sk("stepsHeading")}
        stepWord={tt("mriWalkStepWord")}
        namespace="tests"
        steps={
          test.hasPreConsultation && test.preConsultImage
            ? [
                {
                  titleKey: sk("step1Heading"),
                  descKey: sk("step1Desc"),
                  image: stepsImages[0],
                },
                {
                  titleKey: sk("preConsultHeading"),
                  descKey: sk("preConsultDesc"),
                  image: test.preConsultImage,
                },
                {
                  titleKey: sk("step2Heading"),
                  descKey: sk("step2Desc"),
                  image: stepsImages[1],
                },
                {
                  titleKey: sk("step3Heading"),
                  descKey: sk("step3Desc"),
                  image: stepsImages[2],
                },
              ]
            : [
                {
                  titleKey: sk("step1Heading"),
                  descKey: sk("step1Desc"),
                  image: stepsImages[0],
                },
                {
                  titleKey: sk("step2Heading"),
                  descKey: sk("step2Desc"),
                  image: stepsImages[1],
                },
                {
                  titleKey: sk("step3Heading"),
                  descKey: sk("step3Desc"),
                  image: stepsImages[2],
                },
              ]
        }
        cta={
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:justify-center">
            <ServiceBookingCta
              availability={availability}
              category={ctaCategory}
              productId={bookingProductId}
              trackingLabel={ctaLabel}
              trackingLocation={`${trackingLocation}-steps`}
              className={cn(
                buttonVariants({ variant: "primary" }),
                "w-full sm:w-auto"
              )}
            >
              {ctaLabel}
            </ServiceBookingCta>
            <Link
              href={lp("/contact")}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "w-full sm:w-auto"
              )}
            >
              {ut("talkToTeam")}
            </Link>
          </div>
        }
      />

      {/* ═══════ Section 8: Pricing cards ═══════ */}
      {/* advanced-blood-test drops the bottom padding so the member-priced
          add-ons section below sits close to the comparison cards. Hidden when
          the packages card would advertise a product this market can't book —
          see `showPackagesSection` (prevention-starter in DE). */}
      {showPackagesSection && (
        <section
          className={cn(
            "bg-background py-16 md:py-24",
            test.slug === "advanced-blood-test" && "pb-0 md:pb-0"
          )}
        >
          <div>
            <div className="px-4 md:px-10">
              <h2 className="type-display-md text-center text-neutral-900">
                {test.pricingType === "blood"
                  ? tt("packagesHeadingBlood")
                  : tt("packagesHeading")}
              </h2>
              <p className="type-body-lg mx-auto mt-3 max-w-[500px] text-center text-neutral-900">
                {test.pricingType === "blood"
                  ? tt("packagesSubBlood", {
                      price: packagesFromPriceBlood ?? "",
                    })
                  : tt("packagesSub", { price: packagesFromPrice ?? "" })}
              </p>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-[1440px] px-4 md:px-10">
            {test.pricingType === "mri" ? (
              <MriPackageCards />
            ) : test.slug === "advanced-blood-test" ? (
              // Membership vs one-off comparison — mirrors /pricing + /go/bloodtest
              // so the flagship blood-test page offers the same membership choice.
              <BloodTrackingComparison
                className="mx-auto max-w-[960px]"
                trackingLocation="test-blood"
              />
            ) : (
              <div className="mx-auto max-w-[960px]">
                <BloodTestCard />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Blood add-ons with member prices — advanced-blood-test only, matching
          the /pricing + /go/bloodtest add-on grid. */}
      {test.slug === "advanced-blood-test" && (
        <BloodAddonsSection
          heading={gt("bloodtestAddonsHeading")}
          description={gt("bloodtestAddonsBody")}
          memberPriceLabel={gt("bloodtestAddonsMemberSuffix")}
          maxWidthClassName="max-w-[960px]"
          className="pt-10 pb-16 md:pb-24"
        />
      )}

      {/* Sibling add-ons — on an add-on's own detail page, show the rest of
          the set so people can see what else pairs with their check-up. The
          add-on you're already looking at is filtered out by `titleKey`; the
          full grid stays on /pricing and the main product pages. */}
      {ownImagingAddonTitleKey !== undefined && (
        <ImagingAddonsSection
          servicesConfig={servicesConfig}
          heading={tt("additionalImagingAddons")}
          excludeTitleKeys={
            ownImagingAddonTitleKey ? [ownImagingAddonTitleKey] : undefined
          }
        />
      )}

      {ownBloodAddonTitleKey && (
        <BloodAddonsSection
          heading={tt("additionalBloodAddons")}
          description={gt("bloodtestAddonsBody")}
          memberPriceLabel={gt("bloodtestAddonsMemberSuffix")}
          maxWidthClassName="max-w-[960px]"
          className="pt-10 pb-16 md:pb-24"
          excludeTitleKeys={[ownBloodAddonTitleKey]}
        />
      )}

      {/* Quiz CTA banner — replaces the old "Not sure which?" headline +
          "see pricing" link. Drives undecided users to the personalised
          get.aheadhealth.com quiz instead of the pricing comparison. */}
      <section className="bg-background">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <QuizCtaBanner variant="compact" />
        </div>
      </section>

      {/* ═══════ Section 9: Insurance ═══════ */}
      <InsuranceSection trackingLocation="test-detail-insurance" />

      {/* ═══════ Section 10: Health reports ═══════ */}
      <section className="py-16 md:py-24">
        <div>
          <div className="px-4 md:px-10">
            <h2 className="type-display-md text-center text-neutral-900">
              {tt("reportHeading")}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-[500px] text-center text-neutral-900">
              {tt("reportSub")}
            </p>
            <div className="mt-6 flex justify-center">
              <Link
                href={lp("/report")}
                className={buttonVariants({ variant: "outline" })}
              >
                {ut("exploreReport")}
              </Link>
            </div>
          </div>
        </div>

        {/* iPad report mockup — shared component with hover tooltips */}
        <div>
          <div className="mx-auto mt-12 max-w-6xl px-4 md:px-10">
            <ReportMockupIpad
              bloodOnly={isBloodTestPage}
              explainers={{
                selector: rpt("explainerSelector"),
                nav: rpt("explainerNav"),
                bioAge: rpt("explainerBioAge"),
                heartRisk: rpt("explainerHeartRisk"),
                biomarkers: rpt("explainerBiomarkers"),
                findings: rpt("explainerFindings"),
                summary: rpt("explainerSummary"),
              }}
            />
            <FindingsLegend className="mt-4 justify-center" />
          </div>
        </div>
      </section>

      {/* ═══════ Section 12: Made for each other (cross-sell carousel) ═══════ */}
      {!hideCrossSell && (
        <section className="py-16 md:py-24">
          <div>
            <div className="mx-auto max-w-[1440px] px-4 md:px-10">
              <h2 className="type-display-sm text-neutral-900">
                {tt("madeForEachOther")}
              </h2>
              <p className="type-body-lg mt-4 max-w-2xl text-neutral-500">
                {tt(`${keyPrefix}MadeForEachOtherSub` as never) ||
                  tt("madeForEachOtherSub")}
              </p>
            </div>
          </div>
          <div className="mt-10">
            <ServicesCarousel
              items={translatedServices}
              learnMoreLabel={ut("learnMore")}
              disclaimerLabel={ut("illustrativeSamples")}
            />
          </div>
        </section>
      )}

      {/* ═══════ Section 13: Testimonials ═══════ */}
      {testimonialsSlot ?? (
        <TestimonialsSection
          heading={tt("realResults")}
          description={tt("realResultsSub")}
          cmsTestimonials={initialTestimonials}
        />
      )}

      {/* ═══════ Section 14: Experts ═══════ */}
      {/* Hidden on DE (AHD-6680) and AT — see `hideExperts`. */}
      {experts.length > 0 && !hideExperts && !isBloodTestPage && (
        <section className="py-16 md:py-24">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:gap-12 md:px-10">
            {/* Left column */}
            <div>
              <div>
                <h2 className="type-display-sm text-neutral-900">
                  {tt("expertsHeading")}
                </h2>
                <p className="type-body-lg mt-4 text-neutral-500">
                  {tt("expertsSub")}
                </p>

                {/* Stat card */}
                <div className="bg-background mt-6 rounded-[12px] py-6 pr-6">
                  <p className="type-display-sm leading-[1.12] font-normal text-neutral-900">
                    {tt("expertsStatValue")}
                  </p>
                  <p className="type-body-md mt-1 text-neutral-500">
                    {tt("expertsStatLabel")}
                  </p>
                  <div className="mt-4 flex items-center gap-5">
                    <Image
                      src={blobMediaUrl("/images/logos/oxford.svg")}
                      alt="University of Oxford"
                      width={100}
                      height={25}
                      className="h-6 w-auto opacity-50"
                    />
                    <Image
                      src={blobMediaUrl("/images/logos/karolinska.svg")}
                      alt="Karolinska Institutet"
                      width={66}
                      height={27}
                      className="h-6 w-auto opacity-50"
                    />
                  </div>
                </div>

                <Link
                  href={lp("/about")}
                  className="type-body-lg mt-6 inline-block text-neutral-900 underline"
                >
                  {ut("learnMore")} →
                </Link>
              </div>
            </div>

            {/* Right column: doctors grid (matching homepage) */}
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3">
              {experts.map((doctor) => (
                <div key={doctor.name}>
                  <div className="group relative aspect-[326/397] overflow-hidden rounded-[12px]">
                    <Image
                      src={doctor.image}
                      alt={doctor.name}
                      fill
                      sizes="(max-width: 768px) 50vw, 33vw"
                      className="object-cover"
                    />
                    {/* Dark overlay — fades in on hover */}
                    <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:opacity-40" />
                    {/* Pills container */}
                    <div className="absolute right-2 bottom-2 left-2 z-10 flex flex-col gap-1">
                      {/* Name pill — always visible */}
                      <div className="rounded-[8px] bg-white/[0.77] px-3 py-2.5 backdrop-blur-sm">
                        <p className="type-body-lg leading-snug font-medium text-neutral-900">
                          {doctor.name}
                        </p>
                      </div>
                      {/* Bio pill — expands on hover */}
                      <div className="max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-h-40 group-hover:opacity-100">
                        <div className="rounded-[8px] bg-white/[0.24] px-3 py-2.5">
                          <p className="type-body-md leading-snug text-white">
                            {doctor.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════ Section 15: FAQ ═══════ */}
      {/*
        currentPage="tests/<slug>" matches the CMS pages-tag values like
        "tests/full-body-mri" — so test-specific FAQs (tagged to this exact
        test) emit FAQPage schema here. Generic FAQs that surface on this
        page but are canonical elsewhere (e.g. tagged "homepage") render
        visibly without contributing to this page's schema.
      */}
      {faqSlot ?? (
        <FaqSection
          heading={tt("faqHeading")}
          linkLabel={ut("readAllFaqs")}
          linkHref={lp("/faq")}
          items={initialFaqs}
          currentPage={`tests/${test.slug}`}
        />
      )}

      {/* ═══════ Section 16: CTA banner (full-width, page footer) ═══════ */}
      <section>
        <div className="relative min-h-[400px] overflow-hidden md:min-h-[500px]">
          <Image
            src={blobMediaUrl("/images/tests/cta-window.jpeg")}
            alt=""
            fill
            quality={90}
            sizes="100vw"
            className="object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center p-6 md:p-12">
            <div className="w-full max-w-[520px] rounded-[12px] bg-black/35 p-6 text-center backdrop-blur-[11px] md:p-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="80"
                height="24"
                fill="none"
                viewBox="0 0 124 35"
                className="mx-auto h-6 w-auto text-white"
                role="img"
                aria-label="Ahead Health"
              >
                <path
                  fill="currentColor"
                  d="M14.34 3.216 2.4 32.158h2.945l3.357-8.27h14.303l3.357 8.27h3.11L17.533 3.216H14.34Zm-4.642 18.15 6.176-15.174 6.138 15.173H9.698Zm32.395-11.123c-3.4 0-5.887 1.738-7.089 4.052V2.386H32.27v29.771h2.735V19.796c0-4.714 3.026-7.153 6.344-7.153 3.318 0 5.722 2.108 5.722 6.326V32.16h2.736V18.64c0-5.707-3.151-8.394-7.71-8.394l-.003-.002Zm21.541.002c-5.264 0-10.197 4.052-10.197 11.04 0 6.99 4.56 11.33 10.406 11.33 4.52 0 8.5-2.314 9.743-6.574h-2.735c-1.202 2.77-3.69 4.219-6.924 4.219-4.311 0-7.504-3.432-7.71-8.23h17.495v-.745c0-7.236-4.727-11.04-10.075-11.04h-.003Zm-7.42 9.55c.332-4.508 3.608-7.236 7.379-7.236 3.77 0 7.088 2.728 7.297 7.236H56.214Zm62.651-17.409v12.487c-1.16-2.647-4.063-4.63-7.752-4.63-5.265 0-9.908 4.135-9.908 11.04 0 6.906 4.518 11.33 9.908 11.33 3.689 0 6.634-2.274 7.752-5.044v4.588h2.735V2.387h-2.735Zm-7.381 27.87c-4.228 0-7.462-3.433-7.462-8.973 0-5.54 3.234-8.682 7.462-8.682 3.773 0 7.42 2.77 7.42 8.682 0 5.913-3.689 8.972-7.42 8.972ZM92.677 10.477H77.15v2.737l15.418-.38a2.445 2.445 0 0 1 2.509 2.49l-.335 16.834h2.694V15.225a4.755 4.755 0 0 0-4.76-4.748h.002Z"
                />
                <path
                  fill="currentColor"
                  d="M89.47 16.834 76.677 30.626l1.946 1.99L91.49 18.695a1.366 1.366 0 0 0-.094-1.949 1.375 1.375 0 0 0-1.925.089Z"
                />
              </svg>
              <h2 className="type-headline-md mt-6 text-white">
                {tt("ctaBannerHeading")}
              </h2>
              <div className="mt-6 flex flex-col justify-center gap-3">
                <ServiceBookingCta
                  availability={availability}
                  tone="dark"
                  category={ctaCategory}
                  productId={bookingProductId}
                  trackingLabel={ctaLabel}
                  trackingLocation={`${trackingLocation}-cta-banner`}
                  className={buttonVariants({ variant: "primary" })}
                >
                  {ctaLabel}
                </ServiceBookingCta>
                <Link
                  href={lp("/contact")}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "border-white/40 text-white hover:border-white/60 hover:bg-white/10"
                  )}
                >
                  {ut("talkToTeam")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
